// Tenant-scoped payroll settings panel: Pay Groups + payroll provider
// connections (census sync + match review).
//
// Renders for any caller that knows the target clientId. The employer route
// (/payroll) and the broker's client detail Payroll tab both mount this with
// their respective clientId source — server-side authorization
// (assertClientPayrollAccess in server/routes/payrollRoutes.ts) gates writes
// per role.
//
// Owns only the cards/dialogs — no page header — so the parent screen can
// supply its own framing.

import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Calendar,
  Plus,
  Trash2,
  Link2,
  Clock,
  Save,
  Star,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { getPayCycleLabelCapitalized, getPayCyclePaymentDescription } from "@/lib/payrollUtils";
import { type PayGroup } from "@/hooks/usePaySchedule";
import PayrollConnectWizard from "./PayrollConnectWizard";
import PayrollConnectionCard, { type PayrollConnection } from "./PayrollConnectionCard";
import PayrollMatchReview from "./PayrollMatchReview";
import PayrollDeductionsPanel from "./PayrollDeductionsPanel";

const PAY_FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "semimonthly", label: "Semi-Monthly" },
  { value: "monthly", label: "Monthly" },
];

const PAY_DAYS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
];

/** ISO timestamp -> yyyy-mm-dd for a date input (UTC, no tz drift). */
function toDateInput(iso: string | null | undefined): string {
  return iso ? new Date(iso).toISOString().slice(0, 10) : "";
}

/** yyyy-mm-dd date input -> ISO datetime string the API expects (or null). */
function toIsoOrNull(dateInput: string): string | null {
  return dateInput ? new Date(`${dateInput}T00:00:00.000Z`).toISOString() : null;
}

function PayGroupCard({
  group,
  payGroupsKey,
  canDelete,
}: {
  group: PayGroup;
  payGroupsKey: string;
  canDelete: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    name: group.name,
    payFrequency: group.payFrequency,
    payDay: group.payDay,
    anchorDate: toDateInput(group.periodStartDate),
  });

  const isDirty =
    form.name !== group.name ||
    form.payFrequency !== group.payFrequency ||
    form.payDay !== group.payDay ||
    form.anchorDate !== toDateInput(group.periodStartDate);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [payGroupsKey] });

  const saveMutation = useMutation({
    mutationFn: async () =>
      apiRequest("PUT", `/api/pay-groups/${group.id}`, {
        name: form.name,
        payFrequency: form.payFrequency,
        payDay: form.payDay,
        periodStartDate: toIsoOrNull(form.anchorDate),
      }),
    onSuccess: () => {
      invalidate();
      toast({ title: "Pay group saved", variant: "success" });
    },
    onError: (e: any) => toast({ variant: "destructive", title: "Failed to save", description: e?.message }),
  });

  const makeDefaultMutation = useMutation({
    mutationFn: async () => apiRequest("PUT", `/api/pay-groups/${group.id}`, { isDefault: true }),
    onSuccess: () => {
      invalidate();
      toast({ title: "Default pay group updated", variant: "success" });
    },
    onError: (e: any) => toast({ variant: "destructive", title: "Failed to set default", description: e?.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => apiRequest("DELETE", `/api/pay-groups/${group.id}`),
    onSuccess: () => {
      invalidate();
      toast({ title: "Pay group deleted", variant: "success" });
    },
    onError: (e: any) => toast({ variant: "destructive", title: "Failed to delete", description: e?.message }),
  });

  const busy = saveMutation.isPending || makeDefaultMutation.isPending || deleteMutation.isPending;

  return (
    <div className="p-4 border rounded-lg space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{group.name}</span>
          {group.isDefault && (
            <Badge variant="secondary" className="gap-1">
              <Star className="h-3 w-3" />
              Default
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!group.isDefault && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => makeDefaultMutation.mutate()}
              disabled={busy}
            >
              <Star className="h-4 w-4 mr-2" />
              Make Default
            </Button>
          )}
          {canDelete && !group.isDefault && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => deleteMutation.mutate()}
              disabled={busy}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-2">
          <Label htmlFor={`name-${group.id}`}>Group Name</Label>
          <Input
            id={`name-${group.id}`}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., Salaried — Bi-weekly"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`frequency-${group.id}`}>Pay Frequency</Label>
          <Select value={form.payFrequency} onValueChange={(v) => setForm({ ...form, payFrequency: v })}>
            <SelectTrigger id={`frequency-${group.id}`}>
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              {PAY_FREQUENCIES.map((freq) => (
                <SelectItem key={freq.value} value={freq.value}>
                  {freq.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor={`payday-${group.id}`}>Pay Day</Label>
          <Select value={form.payDay} onValueChange={(v) => setForm({ ...form, payDay: v })}>
            <SelectTrigger id={`payday-${group.id}`}>
              <SelectValue placeholder="Select pay day" />
            </SelectTrigger>
            <SelectContent>
              {PAY_DAYS.map((day) => (
                <SelectItem key={day.value} value={day.value}>
                  {day.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor={`anchor-${group.id}`}>Anchor Pay Date</Label>
            {form.anchorDate && (
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground underline"
                onClick={() => setForm({ ...form, anchorDate: "" })}
              >
                Clear
              </button>
            )}
          </div>
          <Input
            id={`anchor-${group.id}`}
            type="date"
            value={form.anchorDate}
            onChange={(e) => setForm({ ...form, anchorDate: e.target.value })}
          />
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>
            {getPayCycleLabelCapitalized(form.payFrequency)} · {getPayCyclePaymentDescription(form.payFrequency)}
            {!form.anchorDate && " · set an anchor date for exact scheduling"}
          </span>
        </div>
        <Button size="sm" onClick={() => saveMutation.mutate()} disabled={!isDirty || busy}>
          <Save className="h-4 w-4 mr-2" />
          {saveMutation.isPending ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
}

interface ClientPayrollSettingsProps {
  /** Target tenant. Caller supplies the clientId (session for the employer
   * route, URL `:id` for the broker's ClientDetail Payroll tab). */
  clientId: number | undefined;
}

export default function ClientPayrollSettings({ clientId }: ClientPayrollSettingsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const payGroupsKey = clientId ? `/api/clients/${clientId}/pay-groups` : "";
  const { data: payGroups = [] } = useQuery<PayGroup[]>({
    queryKey: [payGroupsKey],
    enabled: !!clientId,
  });

  const connectionsKey = clientId ? `/api/clients/${clientId}/payroll-connections` : "";
  const { data: connections = [] } = useQuery<PayrollConnection[]>({
    queryKey: [connectionsKey],
    enabled: !!clientId,
  });

  const [showConnectWizard, setShowConnectWizard] = useState(false);

  // OAuth callback lands back here with ?payroll=connected|denied|error|invalid_state
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get("payroll");
    if (!outcome) return;
    const messages: Record<string, { title: string; description?: string; variant?: "success" | "destructive" }> = {
      connected: { title: "Payroll connected", description: "The first employee census sync is running.", variant: "success" },
      denied: { title: "Authorization declined", description: "The connection was not authorized.", variant: "destructive" },
      error: { title: "Connection failed", description: "Something went wrong completing the connection.", variant: "destructive" },
      invalid_state: { title: "Connection expired", description: "The authorization link expired — try again.", variant: "destructive" },
    };
    const msg = messages[outcome];
    if (msg) toast(msg);
    params.delete("payroll");
    params.delete("vendor");
    const query = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (query ? `?${query}` : ""));
    queryClient.invalidateQueries({ queryKey: [connectionsKey] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeConnections = connections.filter((c) => c.status !== "disconnected");
  const hasCensus = connections.some((c) => c.lastSyncedAt || c.status === "connected" || c.status === "syncing");

  const addPayGroupMutation = useMutation({
    mutationFn: async () =>
      apiRequest("POST", `/api/clients/${clientId}/pay-groups`, {
        name: `Pay Group ${payGroups.length + 1}`,
        payFrequency: "biweekly",
        payDay: "friday",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [payGroupsKey] });
      toast({ title: "Pay group added", variant: "success" });
    },
    onError: (e: any) => toast({ variant: "destructive", title: "Failed to add pay group", description: e?.message }),
  });

  return (
    <div className="space-y-6">
      {/* Pay Groups Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Pay Groups
            </CardTitle>
            <CardDescription className="mt-1">
              Configure your pay calendars. Each group has its own frequency and anchor pay date;
              employees are assigned a group on their profile (unassigned employees use the default).
            </CardDescription>
          </div>
          <Button onClick={() => addPayGroupMutation.mutate()} variant="outline" size="sm" disabled={addPayGroupMutation.isPending || !clientId}>
            <Plus className="h-4 w-4 mr-2" />
            Add Pay Group
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {payGroups.map((group) => (
              <PayGroupCard
                key={group.id}
                group={group}
                payGroupsKey={payGroupsKey}
                canDelete={payGroups.length > 1}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Payroll provider connections */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Payroll Integrations
            </CardTitle>
            <CardDescription className="mt-1">
              Connect the company's payroll provider to sync the employee census automatically.
            </CardDescription>
          </div>
          <Button onClick={() => setShowConnectWizard(true)} size="sm" disabled={!clientId}>
            <Plus className="h-4 w-4 mr-2" />
            Connect Provider
          </Button>
        </CardHeader>
        <CardContent>
          {activeConnections.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed rounded-lg">
              <Link2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <h3 className="font-medium mb-1">No payroll provider connected</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Connect a provider to pull employees into Administr automatically
              </p>
              <Button variant="outline" onClick={() => setShowConnectWizard(true)} disabled={!clientId}>
                <Plus className="h-4 w-4 mr-2" />
                Connect Provider
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {activeConnections.map((connection) => (
                <PayrollConnectionCard key={connection.id} connection={connection} clientId={clientId!} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Census match review — shown once a provider has synced (or is syncing) */}
      {clientId && hasCensus && <PayrollMatchReview clientId={clientId} />}

      {/* Deduction preview + manual push, per connected write-capable vendor */}
      {clientId &&
        activeConnections
          .filter((c) => c.canPushDeductions && c.status === "connected")
          .map((c) => <PayrollDeductionsPanel key={c.id} connection={c} />)}

      {clientId && (
        <PayrollConnectWizard
          clientId={clientId}
          open={showConnectWizard}
          onClose={() => setShowConnectWizard(false)}
          existingConnections={connections}
        />
      )}
    </div>
  );
}
